import { useState, useEffect, useRef } from 'react'
import { format } from 'date-fns'
import { AlertCircle, ChevronUp, ChevronDown, Search, X } from 'lucide-react'
import { toast } from 'sonner'
import { formatPhoneNumber } from '@intone/shared/utils'

import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useCallerIds,
  useUpdateCallerIdStatus,
  useBulkResetCounters,
  useBulkDeleteCallerIds,
  useBulkMoveCallerIds,
  useTextUploadCallerIds,
} from '@/hooks/useCallerIds'
import type { CallerIdsQueryParams } from '@intone/shared/types'

type SortField = 'caller_phone_number' | 'usage_count' | 'success_count' | 'created_at'
type SortDirection = 'asc' | 'desc'

export function CallerIds() {
  const [offset, setOffset] = useState(0)
  const [limit] = useState(30)
  const [sortField, setSortField] = useState<SortField>('usage_count')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Dialog states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [moveDialogOpen, setMoveDialogOpen] = useState(false)
  const [phoneNumbers, setPhoneNumbers] = useState('')
  const [uploadCampaignId, setUploadCampaignId] = useState<string>('all')
  const [moveCampaignId, setMoveCampaignId] = useState<string>('all')

  // Build query params
  const queryParams: CallerIdsQueryParams = {
    offset,
    limit,
    order_by: sortField,
    order_direction: sortDirection,
    ...(debouncedSearch && { search: debouncedSearch }),
  }

  // Fetch data
  const { data, isLoading, refetch } = useCallerIds(queryParams)
  const updateStatusMutation = useUpdateCallerIdStatus()
  const bulkResetMutation = useBulkResetCounters()
  const bulkDeleteMutation = useBulkDeleteCallerIds()
  const bulkMoveMutation = useBulkMoveCallerIds()
  const textUploadMutation = useTextUploadCallerIds()

  const callerIds = data?.callerIds || []
  const totalCount = data?.totalCount || 0
  const activeCount = data?.activeCount || 0
  const campaigns = data?.campaigns || []

  // Calculate pagination
  const currentPage = Math.floor(offset / limit) + 1
  const totalPages = Math.ceil(totalCount / limit)
  const hasNextPage = offset + limit < totalCount
  const hasPrevPage = offset > 0

  // Create campaign map
  const campaignMap = new Map(campaigns.map((c) => [c.id, c.name]))

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput)
      setOffset(0)
    }, 500)

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchInput])

  const handleNextPage = () => {
    if (hasNextPage) {
      setOffset(offset + limit)
    }
  }

  const handlePrevPage = () => {
    if (hasPrevPage) {
      setOffset(Math.max(0, offset - limit))
    }
  }

  const handleGoToPage = (page: number) => {
    setOffset((page - 1) * limit)
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
    setOffset(0)
  }

  const handleClearSearch = () => {
    setSearchInput('')
    setDebouncedSearch('')
    setOffset(0)
  }

  const handleSwitchChange = (callerId: number, isActive: boolean) => {
    updateStatusMutation.mutate(
      { id: callerId, is_active: isActive },
      {
        onSuccess: (result) => {
          toast.success(result.message)
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'Failed to update status')
        },
      }
    )
  }

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(callerIds.map((c) => c.id)))
    } else {
      setSelectedIds(new Set())
    }
  }

  const handleSelectOne = (id: number, checked: boolean) => {
    const newSelected = new Set(selectedIds)
    if (checked) {
      newSelected.add(id)
    } else {
      newSelected.delete(id)
    }
    setSelectedIds(newSelected)
  }

  const handleBulkResetCounters = () => {
    if (selectedIds.size === 0) return

    bulkResetMutation.mutate(Array.from(selectedIds), {
      onSuccess: (result) => {
        toast.success(result.message)
        setSelectedIds(new Set())
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'Failed to reset counters')
      },
    })
  }

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return

    if (!confirm(`Are you sure you want to delete ${selectedIds.size} caller ID(s)?`)) {
      return
    }

    bulkDeleteMutation.mutate(Array.from(selectedIds), {
      onSuccess: (result) => {
        toast.success(result.message)
        setSelectedIds(new Set())
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'Failed to delete')
      },
    })
  }

  const handleBulkMove = () => {
    if (selectedIds.size === 0) return

    const campaignId = moveCampaignId === 'all' ? null : parseInt(moveCampaignId, 10)

    bulkMoveMutation.mutate(
      { ids: Array.from(selectedIds), campaign_id: campaignId },
      {
        onSuccess: (result) => {
          toast.success(result.message)
          setSelectedIds(new Set())
          setMoveDialogOpen(false)
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'Failed to move')
        },
      }
    )
  }

  const handleTextUpload = () => {
    if (!phoneNumbers.trim()) {
      toast.error('Please enter phone numbers')
      return
    }

    const campaignId = uploadCampaignId === 'all' ? null : parseInt(uploadCampaignId, 10)

    textUploadMutation.mutate(
      { phone_numbers: phoneNumbers, campaign_id: campaignId },
      {
        onSuccess: (result) => {
          toast.success(result.message)
          setPhoneNumbers('')
          setUploadDialogOpen(false)
          refetch()
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'Failed to upload')
        },
      }
    )
  }

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return null
    return sortDirection === 'asc' ? (
      <ChevronUp className="absolute left-[calc(100%+4px)] top-1/2 transform -translate-y-1/2 h-4 w-4" />
    ) : (
      <ChevronDown className="absolute left-[calc(100%+4px)] top-1/2 transform -translate-y-1/2 h-4 w-4" />
    )
  }

  const getCampaignName = (campaignId: number | null) => {
    if (campaignId === null) {
      return 'All Campaigns'
    }
    return campaignMap.get(campaignId) || 'Unknown'
  }

  const isAllSelected = callerIds.length > 0 && selectedIds.size === callerIds.length
  const isSomeSelected = selectedIds.size > 0 && selectedIds.size < callerIds.length
  const isBusy =
    updateStatusMutation.isPending ||
    bulkResetMutation.isPending ||
    bulkDeleteMutation.isPending ||
    bulkMoveMutation.isPending ||
    textUploadMutation.isPending

  return (
    <div className="w-full h-full p-4 overflow-y-auto">
      {/* Header */}
      <div className="mb-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-shrink-0">
            <h1 className="text-heading font-bold text-gray-900 mb-1">Caller IDs Management</h1>
            <p className="text-detail text-gray-600">Manage caller IDs for your campaigns</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="px-3 py-1">
              {activeCount} Active
            </Badge>

            {/* Upload Dialog */}
            <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm">Add Caller IDs</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Caller IDs</DialogTitle>
                  <DialogDescription>
                    Enter phone numbers, one per line or comma-separated
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Phone Numbers</Label>
                    <Textarea
                      value={phoneNumbers}
                      onChange={(e) => setPhoneNumbers(e.target.value)}
                      placeholder="Enter phone numbers..."
                      rows={6}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Campaign (optional)</Label>
                    <Select value={uploadCampaignId} onValueChange={setUploadCampaignId}>
                      <SelectTrigger>
                        <SelectValue placeholder="All Campaigns" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Campaigns</SelectItem>
                        {campaigns.map((c) => (
                          <SelectItem key={c.id} value={String(c.id)}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleTextUpload} disabled={isBusy}>
                    Add
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Caller ID Inventory */}
      <Card className="mb-4">
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
            <div>
              <CardTitle>Caller ID Inventory</CardTitle>
              <CardDescription>
                List of all caller IDs in the system sorted by number of calls made
              </CardDescription>
            </div>
            <div className="flex items-center space-x-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search by phone number..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-10 pr-10 w-64"
                />
                {searchInput && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearSearch}
                    className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Bulk Actions Bar */}
          {selectedIds.size > 0 && (
            <div className="mb-4 flex items-center gap-2 p-3 bg-muted rounded-md">
              <span className="text-body font-medium">{selectedIds.size} selected</span>
              <div className="flex gap-2 ml-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkResetCounters}
                  disabled={isBusy}
                >
                  Reset Counters
                </Button>

                {/* Move Dialog */}
                <Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      Move
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Move Caller IDs</DialogTitle>
                      <DialogDescription>
                        Move {selectedIds.size} caller ID(s) to another campaign
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Target Campaign</Label>
                        <Select value={moveCampaignId} onValueChange={setMoveCampaignId}>
                          <SelectTrigger>
                            <SelectValue placeholder="All Campaigns" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Campaigns</SelectItem>
                            {campaigns.map((c) => (
                              <SelectItem key={c.id} value={String(c.id)}>
                                {c.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setMoveDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleBulkMove} disabled={isBusy}>
                        Move
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <Button variant="destructive" size="sm" onClick={handleBulkDelete} disabled={isBusy}>
                  Delete
                </Button>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : callerIds.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-heading font-medium mb-2">No Caller IDs Found</h3>
              <p className="text-muted-foreground">
                Click "Add Caller IDs" button above to upload phone numbers
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      ref={(input) => {
                        if (input) {
                          input.indeterminate = isSomeSelected
                        }
                      }}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="cursor-pointer"
                    />
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-muted/50 select-none w-full"
                    onClick={() => handleSort('caller_phone_number')}
                  >
                    <span className="relative inline-block">
                      Phone Number{getSortIcon('caller_phone_number')}
                    </span>
                  </TableHead>
                  <TableHead
                    className="text-center cursor-pointer hover:bg-muted/50 select-none whitespace-nowrap"
                    onClick={() => handleSort('usage_count')}
                  >
                    <span className="relative inline-block">
                      Calls Made{getSortIcon('usage_count')}
                    </span>
                  </TableHead>
                  <TableHead
                    className="text-center cursor-pointer hover:bg-muted/50 select-none whitespace-nowrap"
                    onClick={() => handleSort('success_count')}
                  >
                    <span className="relative inline-block">
                      Success{getSortIcon('success_count')}
                    </span>
                  </TableHead>
                  <TableHead
                    className="text-center cursor-pointer hover:bg-muted/50 select-none whitespace-nowrap"
                    onClick={() => handleSort('created_at')}
                  >
                    <span className="relative inline-block">
                      Uploaded{getSortIcon('created_at')}
                    </span>
                  </TableHead>
                  <TableHead className="text-center whitespace-nowrap">Campaign</TableHead>
                  <TableHead className="text-center whitespace-nowrap">Active</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {callerIds.map((callerId) => (
                  <TableRow key={callerId.id}>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(callerId.id)}
                        onChange={(e) => handleSelectOne(callerId.id, e.target.checked)}
                        className="cursor-pointer"
                      />
                    </TableCell>
                    <TableCell className="font-medium text-left w-full">
                      {formatPhoneNumber(callerId.caller_phone_number)}
                    </TableCell>
                    <TableCell className="text-center whitespace-nowrap">
                      {callerId.usage_count}
                    </TableCell>
                    <TableCell className="text-center whitespace-nowrap">
                      {callerId.success_count}{' '}
                      <span className="text-muted-foreground">
                        (
                        {callerId.usage_count > 0
                          ? Math.round((callerId.success_count / callerId.usage_count) * 100)
                          : 0}
                        %)
                      </span>
                    </TableCell>
                    <TableCell className="text-center text-muted-foreground whitespace-nowrap">
                      {format(new Date(callerId.created_at), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell className="text-center text-muted-foreground whitespace-nowrap">
                      {getCampaignName(callerId.campaign_id)}
                    </TableCell>
                    <TableCell className="text-center text-muted-foreground whitespace-nowrap">
                      <Switch
                        checked={callerId.is_active}
                        onCheckedChange={() =>
                          handleSwitchChange(callerId.id, !callerId.is_active)
                        }
                        disabled={isBusy}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Pagination Controls */}
          {callerIds.length > 0 && totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-body text-muted-foreground">
                Showing {offset + 1} to {Math.min(offset + limit, totalCount)} of {totalCount}{' '}
                caller IDs
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handlePrevPage} disabled={!hasPrevPage}>
                  Previous
                </Button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum
                    if (totalPages <= 5) {
                      pageNum = i + 1
                    } else if (currentPage <= 3) {
                      pageNum = i + 1
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i
                    } else {
                      pageNum = currentPage - 2 + i
                    }

                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handleGoToPage(pageNum)}
                        className="min-w-[40px]"
                      >
                        {pageNum}
                      </Button>
                    )
                  })}
                </div>

                <Button variant="outline" size="sm" onClick={handleNextPage} disabled={!hasNextPage}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
